import type { SettingsRegistryEntry } from "@magic-md/engine-client/config";
import { useT } from "@magic-md/i18n/react";
import type { CSSProperties } from "react";
import { cn } from "../../../lib/utils";
import type { ThemeKind } from "../../../theme/contracts";
import {
    compileProfileStyleVariables,
    resolveProfiles,
} from "../../../theme/runtime/profile-resolver";
import { compileThemeStyleVariables } from "../../../theme/runtime/theme-compiler";
import {
    getThemeIconComponent,
    resolveFileIconSpec,
    resolveProductIconSpec,
} from "../../../theme/runtime/theme-icons";
import {
    getColorThemeManifest,
    getFileIconThemeManifest,
    getProductIconThemeManifest,
    getProfileManifest,
    resolveThemeKind,
} from "../../../theme/runtime/theme-resolver";
import { useThemeStore } from "../../../theme/runtime/theme-store";
import { PRODUCT_ICON_PREVIEW_SLOTS } from "./constants";
import {
    getEnumOptionLabel,
    getThemeSelectorEntryKind,
    isThemeRelatedEntryKey,
    summarizeSettingValue,
} from "./format-utils";
import { MiniBadge } from "./LayoutComponents";
import { useSettingsTextResolver } from "./localization";

type EditorTranslate = ReturnType<typeof useT>;

function themeKindLabel(t: EditorTranslate, kind: ThemeKind): string {
    switch (kind) {
        case "light":
            return t("settings.theme.kind.light");
        case "dark":
            return t("settings.theme.kind.dark");
        case "hc-dark":
            return t("settings.theme.kind.hcDark");
        case "hc-light":
            return t("settings.theme.kind.hcLight");
    }
}

function cssVariableStyle(
    styleVariables: Record<string, string>,
): CSSProperties {
    return styleVariables as CSSProperties;
}

export function ThemeSettingContextPanel({
    entry,
    value,
}: {
    entry: SettingsRegistryEntry;
    value: unknown;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const productIconTheme = useThemeStore((state) => state.productIconTheme);
    const fileIconTheme = useThemeStore((state) => state.fileIconTheme);
    const contentProfile = useThemeStore((state) => state.contentProfile);
    const presentationProfile = useThemeStore(
        (state) => state.presentationProfile,
    );
    const resolvedThemeId = useThemeStore((state) => state.resolvedThemeId);
    const resolvedThemeKind = useThemeStore((state) => state.resolvedThemeKind);

    if (!isThemeRelatedEntryKey(entry.key)) {
        return null;
    }

    const selectedSummary =
        typeof value === "string"
            ? getEnumOptionLabel(entry.key, value, resolveText)
            : summarizeSettingValue(entry.key, value, resolveText);

    return (
        <div className="mt-2.5 rounded-md border border-border/70 bg-muted/15 px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                <MiniBadge>
                    {t("settings.theme.currentSelection", { value: selectedSummary })}
                </MiniBadge>
                {entry.key.startsWith("editor.appearance.") ? (
                    <MiniBadge>
                        {t("settings.theme.currentEffective", {
                            value: getColorThemeManifest(resolvedThemeId).label,
                        })}
                    </MiniBadge>
                ) : null}
                {entry.key === "editor.preview.contentProfile" ? (
                    <MiniBadge>
                        {t("settings.theme.currentContentProfile", {
                            value: getProfileManifest(contentProfile, "content").label,
                        })}
                    </MiniBadge>
                ) : null}
                {entry.key === "editor.publish.presentationProfile" ? (
                    <MiniBadge>
                        {t("settings.theme.currentPresentationProfile", {
                            value: getProfileManifest(presentationProfile, "presentation").label,
                        })}
                    </MiniBadge>
                ) : null}
                {entry.key === "editor.appearance.productIconTheme" ? (
                    <MiniBadge>
                        {t("settings.theme.currentProductIconTheme", {
                            value: getProductIconThemeManifest(productIconTheme).label,
                        })}
                    </MiniBadge>
                ) : null}
                {entry.key === "editor.appearance.fileIconTheme" ? (
                    <MiniBadge>
                        {t("settings.theme.currentFileIconTheme", {
                            value: getFileIconThemeManifest(fileIconTheme).label,
                        })}
                    </MiniBadge>
                ) : null}
            </div>
            {entry.key.startsWith("editor.appearance.") ? (
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                    <span>
                        {t("settings.theme.effectiveKind", {
                            kind: themeKindLabel(t, resolvedThemeKind),
                        })}
                    </span>
                </div>
            ) : null}
        </div>
    );
}

export function ThemeSelectorCardEditor({
    entry,
    value,
    disabled,
    onChange,
    onCommit,
}: {
    entry: SettingsRegistryEntry;
    value: string | undefined;
    disabled: boolean;
    onChange: (value: unknown) => void;
    onCommit: (value: unknown) => Promise<void>;
}) {
    const selectorKind = getThemeSelectorEntryKind(entry.key);
    const options = (entry.schema.enum as string[]) ?? [];
    const resolvedThemeId = useThemeStore((state) => state.resolvedThemeId);
    const productIconTheme = useThemeStore((state) => state.productIconTheme);
    const fileIconTheme = useThemeStore((state) => state.fileIconTheme);
    const contentProfile = useThemeStore((state) => state.contentProfile);
    const presentationProfile = useThemeStore(
        (state) => state.presentationProfile,
    );

    if (!selectorKind) {
        return null;
    }

    const activeValue = (() => {
        switch (selectorKind) {
            case "colorTheme":
                return resolvedThemeId;
            case "productIconTheme":
                return productIconTheme;
            case "fileIconTheme":
                return fileIconTheme;
            case "contentProfile":
                return contentProfile;
            case "presentationProfile":
                return presentationProfile;
        }
    })();

    async function handleSelect(nextValue: string) {
        if (disabled) return;
        onChange(nextValue);
        await onCommit(nextValue);
    }

    return (
        <div className="space-y-2.5">
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {options.map((item) => {
                    const selected = value === item;
                    const active = activeValue === item;

                    return (
                        <button
                            key={item}
                            type="button"
                            disabled={disabled}
                            onClick={() => {
                                void handleSelect(item);
                            }}
                            className={cn(
                                "rounded-lg border p-3 text-left transition-colors",
                                selected
                                    ? "border-primary/40 bg-primary/5"
                                    : "border-border/80 bg-background hover:bg-muted/20",
                                disabled && "cursor-not-allowed opacity-60",
                            )}
                        >
                            {selectorKind === "colorTheme" ? (
                                <ColorThemeOptionCard option={item} active={active} />
                            ) : selectorKind === "productIconTheme" ? (
                                <ProductIconThemeOptionCard option={item} active={active} />
                            ) : selectorKind === "fileIconTheme" ? (
                                <FileIconThemeOptionCard option={item} active={active} />
                            ) : selectorKind === "contentProfile" ? (
                                <ProfileOptionCard
                                    option={item}
                                    scope="content"
                                    active={active}
                                    companionProfileId={presentationProfile}
                                />
                            ) : (
                                <ProfileOptionCard
                                    option={item}
                                    scope="presentation"
                                    active={active}
                                    companionProfileId={contentProfile}
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function ColorThemeOptionCard({
    option,
    active,
}: {
    option: string;
    active: boolean;
}) {
    const t = useT("editor");
    const manifest = getColorThemeManifest(option);
    const themeKind = resolveThemeKind(option);
    const previewStyle = cssVariableStyle(
        compileThemeStyleVariables({ themeId: option }).styleVariables,
    );

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-foreground">
                    {manifest.label}
                </span>
                <MiniBadge>{themeKindLabel(t, themeKind)}</MiniBadge>
                {active ? <MiniBadge>{t("settings.theme.active")}</MiniBadge> : null}
            </div>
            <div className="text-[11px] font-mono text-muted-foreground">
                {option}
            </div>
            <div
                className="rounded-md border p-2"
                style={{
                    ...previewStyle,
                    background: "var(--color-background)",
                    color: "var(--color-foreground)",
                    borderColor: "var(--color-border)",
                }}
            >
                <div className="flex items-center justify-between text-[11px]">
                    <span style={{ color: "var(--color-foreground)" }}>{t("settings.theme.preview.sample")}</span>
                    <span style={{ color: "var(--color-muted-foreground)" }}>{t("settings.theme.preview.body")}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                    <span
                        className="rounded px-1.5 py-0.5"
                        style={{
                            background: "var(--color-primary)",
                            color: "var(--color-primary-foreground)",
                        }}
                    >
                        Primary
                    </span>
                    <span
                        className="rounded px-1.5 py-0.5"
                        style={{
                            background: "var(--status-running)",
                            color: "var(--color-background)",
                        }}
                    >
                        Running
                    </span>
                    <span
                        className="rounded px-1.5 py-0.5"
                        style={{
                            background: "var(--status-warning)",
                            color: "var(--color-background)",
                        }}
                    >
                        Warning
                    </span>
                    <span
                        className="rounded px-1.5 py-0.5"
                        style={{
                            background: "var(--status-error)",
                            color: "var(--color-background)",
                        }}
                    >
                        Error
                    </span>
                </div>
            </div>
        </div>
    );
}

function ProductIconThemeOptionCard({
    option,
    active,
}: {
    option: string;
    active: boolean;
}) {
    const t = useT("editor");
    const manifest = getProductIconThemeManifest(option);

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-foreground">
                    {manifest.label}
                </span>
                {active ? <MiniBadge>{t("settings.theme.active")}</MiniBadge> : null}
            </div>
            <div className="text-[11px] font-mono text-muted-foreground">
                {option}
            </div>
            <div className="grid grid-cols-4 gap-2 rounded-md border border-border/80 bg-muted/10 p-2">
                {PRODUCT_ICON_PREVIEW_SLOTS.map((slot) => {
                    const spec = resolveProductIconSpec(option, slot);
                    const Icon = getThemeIconComponent(spec?.name, "File");

                    return (
                        <div key={slot} className="space-y-1 text-center">
                            <div className="flex justify-center text-muted-foreground">
                                <Icon
                                    size={16}
                                    style={spec?.color ? { color: spec.color } : undefined}
                                />
                            </div>
                            <div className="text-[9px] text-muted-foreground">
                                {slot.split(".").pop()}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function FileIconThemeOptionCard({
    option,
    active,
}: {
    option: string;
    active: boolean;
}) {
    const t = useT("editor");
    const manifest = getFileIconThemeManifest(option);
    const samples = [
        {
            label: "md",
            spec: resolveFileIconSpec(option, { name: "README.md" }),
        },
        {
            label: "json",
            spec: resolveFileIconSpec(option, { name: "package.json" }),
        },
        {
            label: "img",
            spec: resolveFileIconSpec(option, { name: "hero.png" }),
        },
        {
            label: "dir",
            spec: resolveFileIconSpec(option, { name: "docs", isDirectory: true }),
        },
        {
            label: "open",
            spec: resolveFileIconSpec(option, {
                name: "docs",
                isDirectory: true,
                isOpen: true,
            }),
        },
        {
            label: "root",
            spec: resolveFileIconSpec(option, {
                name: "workspace",
                isDirectory: true,
                isWorkspaceRoot: true,
            }),
        },
    ];

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-foreground">
                    {manifest.label}
                </span>
                {active ? <MiniBadge>{t("settings.theme.active")}</MiniBadge> : null}
            </div>
            <div className="text-[11px] font-mono text-muted-foreground">
                {option}
            </div>
            <div className="grid grid-cols-6 gap-2 rounded-md border border-border/80 bg-muted/10 p-2">
                {samples.map((sample) => {
                    const Icon = getThemeIconComponent(sample.spec?.name, "File");
                    return (
                        <div key={sample.label} className="space-y-1 text-center">
                            <div className="flex justify-center text-muted-foreground">
                                <Icon
                                    size={16}
                                    style={
                                        sample.spec?.color
                                            ? { color: sample.spec.color }
                                            : undefined
                                    }
                                />
                            </div>
                            <div className="text-[9px] text-muted-foreground">
                                {sample.label}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ProfileOptionCard({
    option,
    scope,
    active,
    companionProfileId,
}: {
    option: string;
    scope: "content" | "presentation";
    active: boolean;
    companionProfileId: string;
}) {
    const t = useT("editor");
    const manifest = getProfileManifest(option, scope);
    const { contentProfile, presentationProfile } = resolveProfiles(
        scope === "content" ? option : companionProfileId,
        scope === "presentation" ? option : companionProfileId,
    );
    const styleVariables = cssVariableStyle(
        compileProfileStyleVariables(contentProfile.id, presentationProfile.id),
    );
    const metrics =
        scope === "content"
            ? [
                manifest.values["content.maxWidth"],
                manifest.values["content.fontSize"],
                manifest.values["content.lineHeight"],
            ]
            : [
                manifest.values["presentation.maxWidth"],
                manifest.values["presentation.fontSize"],
                manifest.values["presentation.lineHeight"],
            ];

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-foreground">
                    {manifest.label}
                </span>
                <MiniBadge>
                    {scope === "content"
                        ? t("settings.theme.scope.content")
                        : t("settings.theme.scope.presentation")}
                </MiniBadge>
                {active ? <MiniBadge>{t("settings.theme.active")}</MiniBadge> : null}
            </div>
            <div className="text-[11px] font-mono text-muted-foreground">
                {option}
            </div>
            <div className="flex flex-wrap gap-1.5 text-[10px] text-muted-foreground">
                {metrics
                    .filter((item): item is string => typeof item === "string")
                    .map((item) => (
                        <MiniBadge key={item}>{item}</MiniBadge>
                    ))}
            </div>
            <div
                className="rounded-md border border-border/80 bg-background p-3"
                style={styleVariables}
            >
                <article
                    className="mx-auto"
                    style={
                        scope === "content"
                            ? {
                                maxWidth: "var(--content-max-width)",
                                fontSize: "var(--content-font-size)",
                                lineHeight: "var(--content-line-height)",
                            }
                            : {
                                maxWidth: "var(--presentation-max-width)",
                                fontSize: "var(--presentation-font-size)",
                                lineHeight: "var(--presentation-line-height)",
                            }
                    }
                >
                    <h4 className="text-sm font-semibold text-foreground">
                        Magic MD Sample
                    </h4>
                    <p className="mt-2 text-xs text-muted-foreground">
                        {t("settings.theme.profileSample")}
                    </p>
                </article>
            </div>
        </div>
    );
}
